// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";

// 권역 대시보드 펑널 단계 클릭 시 열리는 케이스 리스트 모달.
// 기존 GET /api/v1/cases를 region_id/status/flow_filter로 조회 — 펑널 카운트와
// 정확히 일치하는 케이스가 표시되도록 모델 scope(CaseRecord.flow_filtered)를
// 백엔드와 공유한다.

const STATUS_LABEL = {
  applying:   "보류",
  matching:   "매칭",
  assigned:   "배정",
  caring:     "케어중",
  closed:     "종료",
};
const PRIORITY_LABEL = { critical: "높음", warning: "보통", normal: "낮음" };
const PRIORITY_COLOR = { critical: "rose",  warning: "amber",  normal: "gray" };
const RISK_LABEL = { critical: "위험", warning: "주의", stable: "안정" };

function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("ko-KR", { month: "short", day: "numeric" }); }
  catch { return iso; }
}

// 모달 목록 페이지 크기(서버 cases#index PER_PAGE_MAX 이내).
const PER_PAGE = 50;

export function CaseFlowDrilldownModal({ regionId, status, flowFilter, onClose }) {
  useEscapeKey(onClose);
  const [rows, setRows] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({ page: 1, total: 0, total_pages: 1 });
  const [err, setErr] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);
  const [actionErr, setActionErr] = React.useState(null);

  // 보류 단계 케이스만 매칭 단계로 보낼 수 있다(이후 단계는 별도 흐름).
  const canStartMatching = status === "applying";

  // 케이스를 AI 자동매칭(COD) 큐로 전환. 성공 시 후보 매니저가 백엔드에서 자동
  // 생성되며, 이 케이스는 더 이상 현재(보류) 단계가 아니므로 목록에서 뺀다.
  const startMatching = async (e, caseId) => {
    e.stopPropagation();
    setActionErr(null);
    setBusyId(caseId);
    try {
      const r = await fetch(`/api/v1/cases/${caseId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ case: { status: "matching" } }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        const err = new Error(j.message || j.error || `오류 (${r.status})`);
        if (j.error) err.code = j.error;
        throw err;
      }
      setRows(rs => (rs || []).filter(c => c.id !== caseId));
    } catch (err2) {
      setActionErr(apiErrorMessage(err2));
    } finally {
      setBusyId(null);
    }
  };

  React.useEffect(() => {
    const params = new URLSearchParams();
    if (regionId) params.set("region_id", regionId);
    params.set("status", status);
    if (flowFilter && flowFilter !== "all") params.set("flow_filter", flowFilter);
    // 서버 페이지네이션 — 단계 잔여가 수백 건인 권역에서 예전 bare 상한(100건)에
    // 걸려 펑널 숫자(예: 223)와 모달 목록이 어긋나던 문제를 없앤다.
    params.set("page", String(page));
    params.set("per_page", String(PER_PAGE));
    fetch(`/api/v1/cases?${params.toString()}`, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        if (Array.isArray(data)) {   // 구버전 서버 방어
          setRows(data);
          setPagination({ page: 1, total: data.length, total_pages: 1 });
        } else {
          setRows(data.cases || []);
          setPagination(data.pagination || { page: 1, total: 0, total_pages: 1 });
        }
      })
      .catch(e => setErr(apiErrorMessage(e)));
  }, [regionId, status, flowFilter, page]);

  // 단계/필터가 바뀌면 1페이지로.
  React.useEffect(() => { setPage(1); }, [regionId, status, flowFilter]);

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="case-flow-drilldown-title"
      style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:80, display:'grid', placeItems:'center', padding:24}}
      onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} style={{background:'var(--surface)', borderRadius:14, width:'min(720px, 100%)', maxHeight:'min(80vh, 720px)', display:'flex', flexDirection:'column', boxShadow:'0 18px 60px rgba(0,0,0,0.25)', overflow:'hidden'}}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px', borderBottom:'1px solid var(--border-soft)'}}>
          <div>
            <div id="case-flow-drilldown-title" className="h3" style={{margin:0}}>
              {STATUS_LABEL[status] || status} 단계 케이스
              {flowFilter && flowFilter !== "all" && (
                <span className="muted" style={{fontSize:12, fontWeight:500, marginLeft:8}}>
                  · {flowFilter === "critical" ? "높음" : "노쇠 3+"} 필터
                </span>
              )}
            </div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>
              {rows === null
                ? "불러오는 중…"
                : pagination.total_pages > 1
                  ? `전체 ${pagination.total.toLocaleString()}건 · 페이지 ${pagination.page}/${pagination.total_pages}`
                  : `${pagination.total.toLocaleString()}건`}
            </div>
          </div>
          <button type="button" aria-label="닫기" onClick={onClose}
            style={{background:'transparent', border:'none', color:'var(--ink-3)', cursor:'pointer', padding:4}}>
            {Ic.close}
          </button>
        </div>

        <div style={{flex:1, overflow:'auto'}}>
          {err && <div style={{padding:22, color:'var(--rose-500)', fontSize:13}}>케이스를 불러오지 못했습니다: {err}</div>}
          {actionErr && <div style={{padding:'12px 22px 0', color:'var(--rose-500)', fontSize:12.5}}>매칭 전환 실패: {actionErr}</div>}
          {!err && rows && rows.length === 0 && (
            <div className="muted" style={{padding:'32px 22px', textAlign:'center', fontSize:12}}>
              조건에 맞는 케이스가 없습니다.
            </div>
          )}
          {!err && rows && rows.length > 0 && (
            <table className="tbl" style={{width:'100%'}}>
              <thead>
                <tr>
                  <th style={{paddingLeft:22}}>어르신</th>
                  <th>우선순위</th>
                  <th>위험도</th>
                  <th>담당 매니저</th>
                  <th>등록일</th>
                  {canStartMatching && <th style={{textAlign:'right', paddingRight:22}}>액션</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(c => (
                  <tr key={c.id}
                    onClick={() => { window.location.href = `/case?elderly_id=${c.elderly_id}`; }}
                    style={{cursor:'pointer'}}
                    title={`${c.elderly_name || "어르신"} 케이스 상세 보기`}>
                    <td style={{paddingLeft:22}}>
                      <b>{c.elderly_name || "(어르신 없음)"}</b>
                    </td>
                    <td><span className={"badge badge-" + (PRIORITY_COLOR[c.priority] || "gray")}>{PRIORITY_LABEL[c.priority] || c.priority}</span></td>
                    <td><span className="muted" style={{fontSize:12}}>{RISK_LABEL[c.elderly_risk_level] || "-"}</span></td>
                    <td><span className="muted" style={{fontSize:12}}>{c.care_manager_name || "(미배정)"}</span></td>
                    <td className="mono" style={{fontSize:12}}>{fmtDate(c.created_at)}</td>
                    {canStartMatching && (
                      <td style={{textAlign:'right', paddingRight:22}} onClick={(e)=>e.stopPropagation()}>
                        <button type="button" className="btn btn-primary btn-sm"
                          disabled={busyId === c.id}
                          onClick={(e)=>startMatching(e, c.id)}
                          title="이 케이스를 AI 자동매칭(COD) 큐로 보냅니다 (후보 매니저 자동 생성)">
                          {busyId === c.id ? '전환 중…' : '매칭 시작'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{padding:'12px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10}}>
          <a href={`/cases?status=${status}`} className="btn btn-soft btn-md" style={{textDecoration:'none'}}
             title="이 단계를 전체 케이스 목록에서 검색·정렬해 봅니다">전체 케이스 목록에서 보기 →</a>
          {pagination.total_pages > 1 && (
            <div style={{display:'flex', alignItems:'center', gap:8}}>
              <button type="button" className="btn btn-ghost btn-sm" disabled={pagination.page <= 1}
                onClick={()=>setPage(p => Math.max(1, p - 1))}>이전</button>
              <span className="muted" style={{fontSize:12}}>{pagination.page} / {pagination.total_pages}</span>
              <button type="button" className="btn btn-ghost btn-sm" disabled={pagination.page >= pagination.total_pages}
                onClick={()=>setPage(p => Math.min(pagination.total_pages, p + 1))}>다음</button>
            </div>
          )}
          <button type="button" className="btn btn-soft btn-md" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
